import { ChangeDetectorRef, Component, Inject, NgZone, OnInit } from '@angular/core';
import { MAPS_HOST_ADAPTER, MapsHostAdapter, MapsView } from '../../../host-adapter';
import { AdminService } from '../../../services/admin.service';
import type { MapsAdminCompanyRow } from '../../../models/maps.types';

type SortKey =
    | 'companyName'
    | 'activeTokenCount'
    | 'totalSearches'
    | 'searches30d'
    | 'avgLatencyMs'
    | 'aiCost30d'
    | 'exports30d'
    | 'lastSearchAt';

@Component({
    selector: 'maps-admin-dashboard',
    standalone: false,
    templateUrl: './admin-dashboard.component.html',
    styleUrl: './admin-dashboard.component.scss',
})
export class AdminDashboardComponent implements OnInit {
    rows: MapsAdminCompanyRow[] = [];
    isLoading = true;
    errorMessage: string | null = null;

    sortKey: SortKey = 'searches30d';
    sortAsc = false;

    constructor(
        private readonly admin: AdminService,
        @Inject(MAPS_HOST_ADAPTER) private readonly hostAdapter: MapsHostAdapter,
        private readonly zone: NgZone,
        private readonly cdr: ChangeDetectorRef,
    ) {}

    GoTo(view: MapsView): void {
        this.hostAdapter.NavigateTo(view);
    }

    IsActive(view: MapsView): boolean {
        return this.hostAdapter.CurrentLocation === view;
    }

    async ngOnInit(): Promise<void> {
        try {
            const rows = await this.admin.Dashboard();
            this.zone.run(() => {
                this.rows = rows;
                this.isLoading = false;
                this.cdr.detectChanges();
            });
        } catch (err) {
            const detail =
                (err as { message?: string })?.message ??
                (err as { graphQLErrors?: { message?: string }[] })?.graphQLErrors?.[0]?.message ??
                'unknown error';
            // Browser-side instrumentation: MJ logger isn't bundled into the Angular app.
            console.error('[maps.admin] dashboard load failed', err);
            this.zone.run(() => {
                this.errorMessage = `Could not load the dashboard: ${detail}`;
                this.isLoading = false;
                this.cdr.detectChanges();
            });
        }
    }

    get sortedRows(): MapsAdminCompanyRow[] {
        const dir = this.sortAsc ? 1 : -1;
        const key = this.sortKey;
        return [...this.rows].sort((a, b) => {
            const av = a[key];
            const bv = b[key];
            if (key === 'companyName') {
                return dir * String(av ?? '').localeCompare(String(bv ?? ''));
            }
            if (key === 'lastSearchAt') {
                return dir * (Date.parse(String(av ?? 0)) - Date.parse(String(bv ?? 0)));
            }
            return dir * ((Number(av) || 0) - (Number(bv) || 0));
        });
    }

    SetSort(key: SortKey): void {
        if (this.sortKey === key) {
            this.sortAsc = !this.sortAsc;
        } else {
            this.sortKey = key;
            // Text defaults A→Z; numbers/dates default high→low.
            this.sortAsc = key === 'companyName';
        }
    }

    SortIndicator(key: SortKey): string {
        if (this.sortKey !== key) return '';
        return this.sortAsc ? ' ▲' : ' ▼';
    }
}
